import { useState } from "react";
import type { User } from "@revival/contracts";
import { api, type UserHistoryRecord } from "./api";

export default function AccountRecoveryActions({ account, onChanged, onDeleted, deleteLabel = "Permanently delete" }: {
  account: User;
  onChanged: () => Promise<void>;
  onDeleted?: () => Promise<void>;
  deleteLabel?: string;
}) {
  const [history, setHistory] = useState<UserHistoryRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");

  async function viewProfile() {
    if (open) { setOpen(false); return; }
    setWorking(true); setMessage("");
    try { setHistory(await api.userHistory(account.id)); setOpen(true); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Could not open this profile"); }
    finally { setWorking(false); }
  }

  async function recover(permanent: boolean) {
    const confirmation = permanent ? window.prompt(`Permanently delete ${account.name}'s archived account? This cannot be undone. Their sign-in and personal access links will be removed. Projects, snags, photographs and the recorded deletion history remain. A new account will need fresh access assignments.\n\nType ${account.email} to confirm:`) : null;
    if (permanent && confirmation === null) return;
    if (!permanent && !window.confirm(`Restore ${account.name}'s existing ${account.role} profile and retained access?`)) return;
    setWorking(true); setMessage("");
    try {
      if (permanent) await api.purgeArchivedUser(account.id, confirmation!);
      else await api.restoreUser(account.id);
      await onChanged();
      if (permanent && onDeleted) await onDeleted();
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not update this profile"); }
    finally { setWorking(false); }
  }

  return <div className="account-recovery-controls">
    <div className="recovery-action-buttons">
      <button aria-expanded={open} disabled={working} onClick={() => void viewProfile()} type="button">{open ? "Close profile" : "View profile"}</button>
      <button disabled={working} onClick={() => void recover(false)} type="button">Restore profile</button>
      <button className="permanent-delete" disabled={working} onClick={() => void recover(true)} type="button">{deleteLabel}</button>
    </div>
    {message && <p role="alert">{message}</p>}
    {open && history && <section className="archived-profile-details" aria-label={`${account.name}'s profile history`}>
      <h3>{history.user.name}</h3><p>{history.user.email} · {history.user.telephone || "No telephone recorded"} · {history.user.role}</p>
      <h4>Current & previous projects</h4>
      <ul>{history.projects.map((project) => <li key={project.id}><strong>{project.name}</strong> · {project.jobReference || "No reference"} · {project.status} · {project.relationship}</li>)}</ul>
      {!history.projects.length && <p>No retained project relationships.</p>}
      <h4>Invitations</h4>
      <ul>{history.invitations.map((invite) => <li key={invite.id}>{invite.projectName || "Account invitation"} · {new Date(invite.createdAt).toLocaleString("en-GB")} · {invite.revokedAt ? "Revoked" : invite.acceptedAt ? "Accepted" : "Not accepted"}</li>)}</ul>
      {!history.invitations.length && <p>No invitations recorded.</p>}
    </section>}
  </div>;
}
